import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";

const PAGE_SIZE = 24;

export default function MediaPicker({ open, onClose, onSelect, initialQuery = "" }) {
    const [items, setItems] = useState(null);
    const [nextCursor, setNextCursor] = useState(null);
    const [page, setPage] = useState(1);
    const [cursors, setCursors] = useState([null]);
    const [q, setQ] = useState("");

    const load = async (cursor, p, query = q) => {
        setItems(null);
        const qs = `?limit=${PAGE_SIZE}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}${query ? `&q=${encodeURIComponent(query)}` : ""}`;
        const r = await api.get(`/media${qs}`);
        setItems(r.data.items);
        setNextCursor(r.data.next_cursor);
        setPage(p);
    };

    const search = (query) => {
        setQ(query);
        setCursors([null]);
        load(null, 1, query).catch(() => setItems([]));
    };

    useEffect(() => {
        if (open) {
            setCursors([null]);
            setQ(initialQuery);
            load(null, 1, initialQuery).catch(() => setItems([]));
        }
    }, [open]);

    const next = () => {
        if (!nextCursor) return;
        setCursors((c) => [...c, nextCursor]);
        load(nextCursor, page + 1);
    };
    const prev = () => {
        const target = cursors[page - 2] ?? null;
        setCursors((c) => c.slice(0, page - 1));
        load(target, page - 1);
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-3xl" data-testid="media-picker">
                <DialogHeader>
                    <DialogTitle className="font-display">Ortam Kütüphanesinden Seç</DialogTitle>
                    <DialogDescription>Görseller sabit kare oranda gösterilir — seçmek için üzerine tıklayın.</DialogDescription>
                </DialogHeader>
                <input
                    value={q}
                    onChange={(e) => search(e.target.value)}
                    placeholder="Görsel adı ile ara... (örn: pul-biber.jpg)"
                    className="mb-3 h-9 w-full rounded-md border bg-background px-3 text-sm"
                    data-testid="media-picker-search"
                />
                <div className="grid max-h-[420px] grid-cols-3 gap-3 overflow-y-auto scrollbar-thin sm:grid-cols-4 md:grid-cols-6" data-testid="media-picker-grid">
                    {!items && [...Array(12)].map((_, i) => <div key={i} className="aspect-square animate-pulse rounded-lg bg-muted" />)}
                    {items?.map((it) => (
                        <button
                            key={it.id}
                            type="button"
                            onClick={() => onSelect(it.url)}
                            className="group relative aspect-square overflow-hidden rounded-lg border transition-all hover:border-terra-500 hover:ring-2 hover:ring-terra-500/40"
                            data-testid={`media-picker-item-${it.id}`}
                        >
                            <img src={it.url} alt={it.original_filename || ""} className="h-full w-full object-cover" loading="lazy" />
                            {it.used_in && <span className="absolute bottom-0 left-0 right-0 bg-stone-950/70 px-1 py-0.5 text-[9px] text-white truncate">{it.used_in}</span>}
                        </button>
                    ))}
                    {items && !items.length && (
                        <p className="col-span-full py-12 text-center text-sm text-muted-foreground" data-testid="media-picker-empty">{q ? `"${q}" için sonuç bulunamadı.` : "Kütüphanede görsel yok — önce yeni görsel yükleyin."}</p>
                    )}
                </div>
                <DialogFooter className="flex-row items-center justify-between sm:justify-between">
                    <span className="text-xs text-muted-foreground" data-testid="media-picker-page-info">Sayfa {page}</span>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" disabled={page <= 1} onClick={prev} data-testid="media-picker-prev">
                            <ChevronLeft className="mr-1 h-4 w-4" /> Önceki
                        </Button>
                        <Button variant="outline" size="sm" disabled={!nextCursor} onClick={next} data-testid="media-picker-next">
                            Sonraki <ChevronRight className="ml-1 h-4 w-4" />
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
